import { useMemo } from 'react';

/**
 * useAugmentedViewProps Hook
 * 
 * Augments base view props with FOKUS mode and multi-campaign mode overrides.
 * Reduces prop augmentation logic in the main component.
 */
const useAugmentedViewProps = ({
  baseFlowProps,
  baseKanbanProps,
  baseTableProps,
  baseTodoListProps,
  isMultiCampaignMode,
  activeView,
  multiCampaignData,
  kanbanFokus,
  tableFokus,
  handleViewRename,
  handleViewIconChangeLocal,
  onRemoveCampaignFromView,
  onAddCampaignToView,
  removingCampaignId,
}) => {
  // Augment kanban and table with FOKUS mode
  const kanbanProps = useMemo(() => ({
    ...baseKanbanProps,
    fokusMode: kanbanFokus,
  }), [baseKanbanProps, kanbanFokus]);

  const tableProps = useMemo(() => ({
    ...baseTableProps,
    fokusMode: tableFokus,
  }), [baseTableProps, tableFokus]);

  // Augment flow props with multi-campaign mode
  const flowProps = useMemo(() => {
    if (!isMultiCampaignMode) return baseFlowProps;
    return {
      ...baseFlowProps,
      isMultiCampaignMode: true,
      activeView,
      multiCampaignCampaigns: multiCampaignData?.campaigns,
      onViewRename: handleViewRename,
      onViewIconChange: handleViewIconChangeLocal,
      onRemoveCampaign: onRemoveCampaignFromView,
      onAddCampaignToView,
      removingCampaignId,
    };
  }, [baseFlowProps, isMultiCampaignMode, activeView, multiCampaignData, handleViewRename, handleViewIconChangeLocal, onRemoveCampaignFromView, onAddCampaignToView, removingCampaignId]);

  // Augment kanban props with multi-campaign mode
  const augmentedKanbanProps = useMemo(() => {
    if (!isMultiCampaignMode) return kanbanProps;
    return {
      ...kanbanProps,
      campaignName: activeView?.name || 'Saved View',
      campaignIcon: activeView?.icon || '👁️',
      breadcrumbPath: [],
      onRenameSprint: handleViewRename,
      onSprintIconChange: handleViewIconChangeLocal,
    };
  }, [kanbanProps, isMultiCampaignMode, activeView, handleViewRename, handleViewIconChangeLocal]);

  // Augment table props with multi-campaign mode
  const augmentedTableProps = useMemo(() => {
    if (!isMultiCampaignMode) return tableProps;
    return {
      ...tableProps,
      campaignName: activeView?.name || 'Saved View',
      campaignIcon: activeView?.icon || '👁️',
      breadcrumbPath: [],
      onRenameSprint: handleViewRename,
      onSprintIconChange: handleViewIconChangeLocal,
    };
  }, [tableProps, isMultiCampaignMode, activeView, handleViewRename, handleViewIconChangeLocal]);

  // Augment todoList props with multi-campaign mode
  const todoListProps = useMemo(() => {
    if (!isMultiCampaignMode) return baseTodoListProps;
    return {
      ...baseTodoListProps,
      campaignName: activeView?.name || 'Saved View',
      campaignIcon: activeView?.icon || '👁️',
      breadcrumbPath: [],
    };
  }, [baseTodoListProps, isMultiCampaignMode, activeView]);

  return {
    flowProps,
    augmentedKanbanProps,
    augmentedTableProps,
    todoListProps,
  };
};

export default useAugmentedViewProps;
